import axios, { AxiosResponse } from 'axios'
import type { 
  ParentDashboardData, 
  Payment, 
  Expense, 
  LessonData,
  ApiResponse 
} from '../types/api'

// API Base Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Helper function to validate JWT format and expiration
const isValidJWT = (token: string): boolean => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3 || !parts.every(part => part.length > 0)) {
      return false
    }
    
    // Check if it's from the old mock implementation
    const payload = JSON.parse(atob(parts[1]))
    
    // Reject tokens with "John Doe" name (from mock implementation)
    if (payload.name === 'John Doe') {
      console.warn('Detected old mock token, clearing')
      return false
    }
    
    // Check if token is expired
    if (payload.exp && payload.exp < Date.now() / 1000) {
      console.warn('Token expired, clearing')
      return false
    }
    
    return true
  } catch (e) {
    return false
  }
}

// Force clear all auth-related data on app start
const clearAllAuthData = () => {
  localStorage.removeItem('auth-storage')
  localStorage.removeItem('krudream-app-version')
  console.log('Cleared all auth data for fresh OAuth implementation')
}

// Check and clear old data on first load
const APP_VERSION = '2.1.0'
const currentVersion = localStorage.getItem('krudream-app-version')
if (currentVersion !== APP_VERSION) {
  clearAllAuthData()
  localStorage.setItem('krudream-app-version', APP_VERSION)
}

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    // Get token from localStorage (zustand persist)
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      try {
        const { state } = JSON.parse(authStorage)
        if (state?.token) {
          // Validate JWT format and content before using
          if (isValidJWT(state.token)) {
            config.headers.Authorization = `Bearer ${state.token}`
          } else {
            // Clear malformed/expired token
            console.warn('Invalid JWT token detected, clearing all auth data')
            clearAllAuthData()
            // Force redirect to login if on a protected page
            if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/auth/')) {
              window.location.href = '/login'
            }
          }
        }
      } catch (error) {
        console.warn('Failed to parse auth storage, clearing all auth data:', error)
        clearAllAuthData()
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - could trigger logout
      console.warn('Unauthorized access - token may be expired')
      // For mock API, we'll just log and continue
    }
    return Promise.reject(error)
  }
)

// Mock data fallbacks for development
const MOCK_PARENT_DASHBOARD: ParentDashboardData = {
  student: {
    id: 'STU-001',
    name: 'น้องใหม่',
    level: 'ป.3',
    subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ']
  },
  payments: {
    current_month: {
      amount: 1600,
      status: 'pending',
      due_date: '2024-09-01',
      invoice_id: 'INV-2568-09-001'
    },
    overdue_amount: 0,
    next_due_date: '2024-10-01'
  },
  lessons: {
    latest: {
      date: '2024-08-17',
      subjects_taught: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
      homework_assigned: true
    },
    total_this_month: 4
  },
  expenses: {
    pending_amount: 145,
    this_month_total: 320
  },
  announcements: [
    {
      id: '1',
      title: 'เปิดเทอมวันที่ 1 กันยายน 2568',
      message: 'ขอให้นักเรียนทุกคนมาตรงเวลา',
      date: '2024-08-15',
      priority: 'normal'
    }
  ]
}

// API Service Functions
export const apiService = {
  // Dashboard
  async getParentDashboard(): Promise<ParentDashboardData> {
    try {
      const response = await apiClient.get<ApiResponse<ParentDashboardData>>('/dashboard/parent')
      return response.data.data
    } catch (error) {
      console.warn('Using mock data for parent dashboard:', error)
      return MOCK_PARENT_DASHBOARD
    }
  },

  // Payments
  async getPayments(studentId: string, filters?: { status?: string; month?: string }): Promise<Payment[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.status) params.append('status', filters.status)
      if (filters?.month) params.append('month', filters.month)
      
      const response = await apiClient.get<ApiResponse<Payment[]>>(`/payments/student/${studentId}?${params}`)
      return response.data.data
    } catch (error) {
      console.warn('Using mock data for payments:', error)
      return [
        {
          id: 'PAY-001',
          invoice_id: 'INV-2568-09-001',
          student_id: studentId,
          amount: 1600,
          status: 'pending',
          due_date: '2024-09-01',
          created_at: '2024-08-15T10:00:00Z'
        }
      ]
    }
  },

  // Expenses
  async getExpenses(studentId: string, filters?: { status?: string; date_from?: string; date_to?: string }): Promise<Expense[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.status) params.append('status', filters.status)
      if (filters?.date_from) params.append('date_from', filters.date_from)
      if (filters?.date_to) params.append('date_to', filters.date_to)
      
      const response = await apiClient.get<ApiResponse<Expense[]>>(`/expenses/student/${studentId}?${params}`)
      return response.data.data
    } catch (error) {
      console.warn('Using mock data for expenses:', error)
      return [
        {
          id: 'EXP-001',
          student_id: studentId,
          category: 'snacks',
          amount: 145,
          description: 'ขนมและเครื่องดื่ม',
          date: '2024-08-17',
          status: 'pending',
          receipt_url: null,
          created_at: '2024-08-17T12:30:00Z'
        }
      ]
    }
  },

  // Lessons
  async getLessons(filters?: { session_group?: string; subject?: string; limit?: number }): Promise<LessonData[]> {
    try {
      const params = new URLSearchParams()
      if (filters?.session_group) params.append('session_group', filters.session_group)
      if (filters?.subject) params.append('subject', filters.subject)
      if (filters?.limit) params.append('limit', filters.limit.toString())
      
      const response = await apiClient.get<ApiResponse<LessonData[]>>(`/lessons/latest?${params}`)
      return response.data.data
    } catch (error) {
      console.warn('Using mock data for lessons:', error)
      return [
        {
          id: 'LESSON-001',
          date: '2024-08-17',
          session_time: '09:00-15:00',
          subjects: {
            mathematics: {
              topic: 'การบวกลบเลข 3 หลัก',
              content: 'ทบทวนการบวกตัวเลข 3 หลัก\nการทดในหลักสิบ\nแบบฝึกหัด 10 ข้อ',
              homework: 'แบบฝึกหัดหน้า 25 ข้อ 1-10',
              attachments: []
            },
            thai: {
              topic: 'การผันวรรณยุกต์',
              content: 'วรรณยุกต์ 5 เสียง\nฝึกผันคำ\nอ่านเรื่อง "บ้านของเรา"',
              homework: 'อ่านเรื่อง "บ้านของเรา" และตอบคำถามท้ายเรื่อง',
              attachments: []
            },
            english: {
              topic: 'Present Simple Tense',
              content: 'I, You, We, They + V1\nHe, She, It + Vs/es\nVocabulary: Family members',
              homework: 'Workbook page 15-16',
              attachments: []
            }
          },
          status: 'published',
          published_at: '2024-08-17T14:30:00Z',
          students_notified: 18
        }
      ]
    }
  },

  // Upload payment slip
  async uploadPaymentSlip(invoiceId: string, file: File): Promise<{ success: boolean; upload_url?: string }> {
    try {
      // First get presigned URL
      const urlResponse = await apiClient.get(`/uploads/presigned-url`, {
        params: {
          file_type: 'payment_slip',
          file_extension: file.name.split('.').pop(),
          content_type: file.type
        }
      })
      
      // Then upload the file (in real implementation)
      // For mock, we'll just simulate success
      console.log('Mock payment slip upload for invoice:', invoiceId)
      return { success: true, upload_url: 'mock://payment-slip-url' }
    } catch (error) {
      console.warn('Mock payment slip upload failed:', error)
      return { success: false }
    }
  },

  // Self-Service Enrollment
  async getCourses(activeOnly: boolean = true): Promise<any[]> {
    try {
      const params = new URLSearchParams()
      if (!activeOnly) params.append('active', 'false')
      
      const response = await apiClient.get(`/courses?${params}`)
      return response.data.data.courses
    } catch (error) {
      console.warn('Failed to fetch courses, using mock data:', error)
      
      // Mock course data matching the backend structure
      return [
        {
          id: 1,
          name: 'คอร์สเสาร์หลัก',
          description: 'เรียนทุกวันเสาร์ เวลา 09:00-15:00 เหมาะสำหรับนักเรียน ป.3 ที่ต้องการพื้นฐานที่แข็งแกร่ง',
          price: 1600,
          schedule: 'ทุกวันเสาร์ 09:00-15:00',
          subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
          max_capacity: 25,
          is_active: true,
          capacity: {
            max_capacity: 25,
            current_enrollments: 22,
            available_spots: 3,
            is_full: false
          }
        },
        {
          id: 2,
          name: 'คอร์สเสาร์-อาทิตย์',
          description: 'เรียนเสาร์และอาทิตย์ เพิ่มวิทยาศาสตร์ เหมาะสำหรับนักเรียนที่ต้องการเรียนเพิ่มเติม',
          price: 2400,
          schedule: 'วันเสาร์-อาทิตย์ 09:00-15:00',
          subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ', 'วิทยาศาสตร์'],
          max_capacity: 20,
          is_active: true,
          capacity: {
            max_capacity: 20,
            current_enrollments: 15,
            available_spots: 5,
            is_full: false
          }
        },
        {
          id: 3,
          name: 'คอร์สเสาร์เข้ม',
          description: 'เรียนเสาร์ เพิ่มชั่วโมงเรียน เน้นการบ้านและทดสอบ เหมาะสำหรับการเตรียมสอบ',
          price: 1800,
          schedule: 'ทุกวันเสาร์ 08:00-16:00',
          subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ', 'แบบฝึกหัดเพิ่มเติม'],
          max_capacity: 15,
          is_active: true,
          capacity: {
            max_capacity: 15,
            current_enrollments: 12,
            available_spots: 3,
            is_full: false
          }
        }
      ]
    }
  },

  async getCourseDetails(courseId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/courses/${courseId}`)
      return response.data.data
    } catch (error) {
      console.warn(`Failed to fetch course ${courseId}, using mock data:`, error)
      
      // Return mock course details based on ID
      const mockCourses = await this.getCourses()
      return mockCourses.find(course => course.id === courseId) || null
    }
  },

  async createEnrollment(enrollmentData: {
    parent_id: number;
    student_name: string;
    nickname: string;
    course_id: number;
    month_year: string;
  }): Promise<any> {
    try {
      const response = await apiClient.post('/enrollments', enrollmentData)
      return response.data.data
    } catch (error) {
      console.warn('Failed to create enrollment, using mock response:', error)
      
      // Mock enrollment response
      return {
        id: Date.now(),
        ...enrollmentData,
        payment_status: 'pending',
        enrolled_at: new Date().toISOString(),
        amount: 1600 // Mock amount
      }
    }
  },

  async renewEnrollment(parentId: number, monthYear: string): Promise<any> {
    try {
      const response = await apiClient.post('/enrollments/renew', {
        parent_id: parentId,
        month_year: monthYear
      })
      return response.data.data
    } catch (error) {
      console.warn('Failed to renew enrollment, using mock response:', error)
      
      // Mock renewal response
      return {
        id: Date.now(),
        parent_id: parentId,
        student_name: 'ด.ช.สมชาย ใจดี',
        nickname: 'ชาย',
        course_id: 1,
        month_year: monthYear,
        payment_status: 'pending',
        enrolled_at: new Date().toISOString(),
        amount: 1600,
        renewed_from: 'คอร์สเสาร์หลัก'
      }
    }
  },

  async getLastEnrollment(parentId: number): Promise<any> {
    try {
      const response = await apiClient.get(`/enrollments/last?parent_id=${parentId}`)
      return response.data.data
    } catch (error) {
      console.warn('Failed to get last enrollment, using mock response:', error)
      
      // Mock last enrollment (return null for first-time parents)
      return null
    }
  },

  async getParentEnrollments(parentId: number): Promise<any[]> {
    try {
      const response = await apiClient.get(`/enrollments?parent_id=${parentId}`)
      return response.data.data.enrollments
    } catch (error) {
      console.warn('Failed to get parent enrollments, using mock data:', error)
      
      // Mock enrollments
      return [
        {
          id: 1,
          parent_id: parentId,
          student_name: 'ด.ช.สมชาย ใจดี',
          nickname: 'ชาย',
          course_id: 1,
          course_name: 'คอร์สเสาร์หลัก',
          course_schedule: 'ทุกวันเสาร์ 09:00-15:00',
          course_subjects: ['คณิตศาสตร์', 'ภาษาไทย', 'ภาษาอังกฤษ'],
          month_year: '2025-08',
          amount: 1600,
          payment_status: 'paid',
          enrolled_at: '2025-08-01T10:00:00.000Z'
        }
      ]
    }
  },

  async uploadEnrollmentPaymentSlip(enrollmentId: number, paymentSlipUrl: string): Promise<any> {
    try {
      const response = await apiClient.post(`/enrollments/${enrollmentId}/payment`, {
        payment_slip_url: paymentSlipUrl
      })
      return response.data.data
    } catch (error) {
      console.warn('Failed to upload enrollment payment slip, using mock response:', error)
      
      // Mock payment slip upload
      return {
        id: enrollmentId,
        payment_slip_url: paymentSlipUrl,
        payment_status: 'pending'
      }
    }
  }
}

// Error handling utility
export const handleApiError = (error: any) => {
  if (error.response) {
    // Server responded with error status
    const message = error.response.data?.error?.message || 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์'
    return { message, status: error.response.status }
  } else if (error.request) {
    // Request was made but no response received
    return { message: 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้', status: 0 }
  } else {
    // Something else happened
    return { message: 'เกิดข้อผิดพลาดไม่ทราบสาเหตุ', status: -1 }
  }
}

export { apiClient }
export default apiService